import { useCallback, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import SideBarModal from '../modal/SideBarModal';
import { FooterAction } from '../../interfaces';
import useGetCustomerOverview from '../../hooks/customers/useGetCustomerOverview';
import { createModalData } from '../../utils/modal/customerModal';
import { createFooterAction } from '../../helpers/global';
import {
  CustomerFileForm,
  customerFileSchema,
  CustomerOverview,
} from '../../types/customers';
import FormSmallModal, { FormData } from '../global/FormSmallModal';
import { ACCEPTED_FILE_TYPES } from '../../constants/customers';
import useUploadCustomerFile from '../../hooks/customers/useUploadCustomerFile';
import useDeleteCustomerFile from '../../hooks/customers/useDeleteCustomerFile';
import useConfirm from '../../hooks/useConfirm';

interface Props {
  customerId: string | null;
  isOpen: boolean;
  onClose: () => void;
  onEditCustomer: (customer: CustomerOverview) => void;
}

const constructCustomerName = (customer: CustomerOverview) =>
  `${customer.user.firstName || ''} ${customer.user.lastName || ''}`.trim();

function CustomerSideBarModal({
  customerId,
  isOpen,
  onClose,
  onEditCustomer,
}: Props) {
  const open = !!customerId && isOpen;
  const confirm = useConfirm();
  const [isFileUploadModalOpen, setIsFileUploadModalOpen] = useState(false);

  const {
    data: customer,
    isLoading,
    isError,
    isPlaceholderData,
    refetch,
  } = useGetCustomerOverview(customerId, {
    enabled: open,
  });

  const onError = useCallback((type: 'upload' | 'delete') => {
    toast.error(`Failed to ${type} file`);
  }, []);

  const { mutateAsync: uploadCustomerFile, isPending: isUploading } =
    useUploadCustomerFile({
      refetch,
      onSuccess: () => setIsFileUploadModalOpen(false),
      onError: () => onError('upload'),
    });

  const {
    mutate: deleteCustomerFile,
    isPending: isDeleting,
    variables: deleteVariables,
  } = useDeleteCustomerFile({
    refetch,
    onError: () => onError('delete'),
  });
  const fileIdBeingDeleted = isDeleting ? deleteVariables?.fileId : undefined;

  const handleDeleteFile = useCallback(
    async (fileId: string) => {
      if (!customerId) return;

      const confirmed = await confirm({
        title: 'Delete file',
        message:
          'Are you sure you want to delete this file? This action cannot be undone.',
      });
      if (!confirmed) return;

      deleteCustomerFile({
        customerId,
        fileId,
      });
    },
    [customerId, deleteCustomerFile, confirm],
  );

  const customerName =
    customer && !isError ? constructCustomerName(customer) : '-';
  const modalData = useMemo(
    () =>
      customer
        ? createModalData(customer, {
            deleteFile: handleDeleteFile,
            fileIdBeingDeleted,
          })
        : null,
    [customer, fileIdBeingDeleted, handleDeleteFile],
  );

  const footerActions = [
    !!customer &&
      createFooterAction('Edit Customer', () => {
        if (!customer) return;
        onEditCustomer(customer);
      }),
  ].filter(Boolean) as FooterAction[];

  const moreActions = useMemo(
    () =>
      [
        !!customer && {
          label: 'Upload file for customer',
          action: () => setIsFileUploadModalOpen(true),
        },
      ].filter(Boolean) as FooterAction[],
    [customer],
  );

  const fileUploadForm: FormData<CustomerFileForm> = {
    initialValues: {
      file: null as any,
    },
    inputRows: [
      [
        {
          inputType: 'file',
          label: 'Upload file',
          name: 'file',
          accept: ACCEPTED_FILE_TYPES,
          acceptLabel: 'Image, PDF',
          acceptLabelNewLine: true,
          acceptLabelSx: {
            textAlign: 'center',
          },
          disabled: isUploading,
        },
      ],
    ],
    onSubmit: async values => {
      if (!customerId || !values.file) return;
      await uploadCustomerFile({
        customerId,
        file: values.file,
      });
    },
    submitButtonText: 'Upload',
    title: 'Upload file for customer',
    validationSchema: customerFileSchema,
  };

  if (isLoading || isPlaceholderData) {
    return null;
  }

  return (
    <>
      <SideBarModal
        modalOpen={open}
        setModalOpen={onClose}
        name={customerName}
        sections={
          isError
            ? [
                {
                  name: '',
                  rows: [
                    {
                      label: '',
                      type: 'TEXT',
                      value: ['There was an error loading this customer.'],
                    },
                  ],
                  values: [],
                },
              ]
            : modalData?.sections ?? []
        }
        moreActions={moreActions}
        footerActions={footerActions}
      />
      {isFileUploadModalOpen && (
        <FormSmallModal
          maxWidth="480px"
          form={fileUploadForm}
          open
          onClose={() => setIsFileUploadModalOpen(false)}
        />
      )}
    </>
  );
}

export default CustomerSideBarModal;
